import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { TimePeriod } from '@modules/charts/components/TimePeriod';
import { getTimeBucketCountFromAggregatePeriod } from '@modules/common/utils';
import {
  MAX_DAILY_BUCKET_COUNT_FOR_TREND_CHART,
  MAX_WEEKLY_BUCKET_COUNT_FOR_TREND_CHART,
  MIN_TIME_BUCKET_COUNT_FOR_TREND_CHART,
} from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useTriggerQuantifyQueryAnalyticsEvent,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { AggregatePeriod } from 'v2/core/entities';
import { shallow } from 'zustand/shallow';

const trackQuantifyReportEvents = createAnalyticsTracker('quantifyReport');

export const QuantifyTrendChartTimePeriod = () => {
  const [shouldExecuteQuery, setShouldExecuteQuery] = useState(false);

  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const duration = useQuantifyStore(({ query }) => query.duration);
  const aggregatePeriod = useQuantifyStore(
    ({ metadata }) => metadata.aggregatePeriod,
  );
  const groupByLabel = useQuantifyStore(
    ({ query }) => query.groupBy?.displayName,
  );

  const fetchReportData = useFetchQuantifyReport();

  const quantifyQueryAnalyticsEvent = useTriggerQuantifyQueryAnalyticsEvent();

  const { title: name, href } = useAnalysisStore(
    ({ title, href, scope }) => ({ title, href, scope }),
    shallow,
  );

  useUpdateEffect(() => {
    if (!shouldExecuteQuery) {
      return;
    }
    setShouldExecuteQuery(false);
    fetchReportData();
    quantifyQueryAnalyticsEvent('Quantify - Query Executed');
  }, [shouldExecuteQuery]);

  const onAggregatePeriodChange = (newAggregatePeriod: AggregatePeriod) => {
    dispatch({
      type: QuantifyActionType.SET_METADATA,
      payload: {
        aggregatePeriod: newAggregatePeriod,
      },
    });

    setShouldExecuteQuery(true);

    trackQuantifyReportEvents({
      eventName: 'Quantify Report - Trend Chart Time-Period Changed',
      name,
      link: href,
      query: {
        showMeLabel: groupByLabel,
      },
      aggregatePeriod: newAggregatePeriod,
    });
  };

  const getDisableTimePeriodObject = () => {
    const dayDiff = getTimeBucketCountFromAggregatePeriod(
      AggregatePeriod.DAY,
      duration,
    );
    const weekDiff = getTimeBucketCountFromAggregatePeriod(
      AggregatePeriod.WEEK,
      duration,
    );
    const monthDiff = getTimeBucketCountFromAggregatePeriod(
      AggregatePeriod.MONTH,
      duration,
    );

    return {
      [AggregatePeriod.DAY]: dayDiff > MAX_DAILY_BUCKET_COUNT_FOR_TREND_CHART,
      [AggregatePeriod.WEEK]:
        weekDiff > MAX_WEEKLY_BUCKET_COUNT_FOR_TREND_CHART ||
        weekDiff < MIN_TIME_BUCKET_COUNT_FOR_TREND_CHART,
      [AggregatePeriod.MONTH]:
        monthDiff < MIN_TIME_BUCKET_COUNT_FOR_TREND_CHART,
    };
  };

  return (
    <TimePeriod
      value={aggregatePeriod}
      onChange={onAggregatePeriodChange}
      disable={getDisableTimePeriodObject()}
    />
  );
};
